import React, { useState, useEffect, useMemo } from 'react';
import { useChainContext } from '../contexts/ChainContext';
import BlockchainService from '../services/blockchainService';
import { getChainConfig } from '../config/chainConfig';
import { ChevronDown } from 'lucide-react';

function Footer() {
  const { currentChain } = useChainContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [apiStatus, setApiStatus] = useState({ status: 'unknown', message: 'Initializing...' });
  const [stats, setStats] = useState({
    latency: null,
    lastResponse: null
  });
  const [diagMessage, setDiagMessage] = useState(null);

  // Memoizar instancia del servicio de blockchain
  const blockchainService = useMemo(
    () => new BlockchainService(currentChain),
    [currentChain]
  );

  const chainConfig = useMemo(
    () => getChainConfig(currentChain),
    [currentChain]
  );

  // Update API status periodically from backend
  useEffect(() => {
    const updateApiStatus = async () => {
      if (blockchainService) {
        try {
          const response = await fetch(`${blockchainService.apiUrl}/${currentChain}/api_status`);
          const status = await response.json();
          setApiStatus(status);
          
          setStats(prev => ({ ...prev, lastResponse: new Date() }));
        } catch (error) {
          console.error('Error fetching API status:', error);
          setApiStatus({ 
            status: 'unknown', 
            message: 'Unable to fetch API status',
            endpoint: blockchainService?.apiUrl || 'Unknown'
          });
        }
      }
    };

    updateApiStatus();
    const statusInterval = setInterval(() => {
      if (!document.hidden) updateApiStatus(); // no sondear en pestañas ocultas
    }, 5000);

    return () => {
      clearInterval(statusInterval);
    };
  }, [blockchainService, currentChain]);

  // Punto de color + texto (sin emojis)
  const getStatusConfig = (status) => {
    switch (status) {
      case 'healthy':
        return { dot: 'bg-green-600', text: 'API healthy' };
      case 'degraded':
        return { dot: 'bg-amber-500', text: 'API degraded' };
      case 'circuit_open':
        return { dot: 'bg-red-600', text: 'API offline' };
      case 'initializing':
        return { dot: 'bg-indigo-500', text: 'Connecting…' };
      default:
        return { dot: 'bg-gray-400', text: 'API status unknown' };
    }
  };

  const statusConfig = getStatusConfig(apiStatus.status);
  const chainName = chainConfig?.name || 'Unknown Chain';
  const externalApiUrl = apiStatus.endpoint || 'Unknown';

  const runDiagnostics = async () => {
    if (!blockchainService) return;
    setDiagMessage({ type: 'running', text: 'Running diagnostics...' });
    try {
      const startTime = Date.now();
      await blockchainService.fetchFromChain('/info');
      const latency = Date.now() - startTime;

      setStats(prev => ({
        ...prev,
        latency,
        lastResponse: new Date()
      }));

      setDiagMessage({ type: 'success', text: `Connected — latency ${latency}ms` });
    } catch (error) {
      setDiagMessage({ type: 'error', text: `Failed: ${error.message}` });
    }
  };

  return (
    <footer className="border-t border-gray-200 bg-surface">
      <div className="page-container py-5">
        <div className="flex flex-wrap gap-3 justify-between items-center text-[13px] text-gray-500">
          <span>
            © {new Date().getFullYear()} ChainScout · {chainName} explorer and wallet by{' '}
            <a href="https://eosiomadrid.io" target="_blank" rel="noopener noreferrer" className="link">EOSIO Madrid</a>
          </span>
          {/* Estado de la API; clic para ver detalles */}
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
            className="flex items-center gap-1.5 hover:text-gray-900"
            title="Click for detailed API information"
          >
            <span aria-hidden="true" className={`w-2 h-2 rounded-full ${statusConfig.dot}`} />
            {statusConfig.text}
            <ChevronDown size={14} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Expanded Details Panel */}
        {isExpanded && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
              {/* API Information */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">API Information</h4>
                <div className="space-y-1">
                  <div>
                    <span className="text-gray-500">Endpoint:</span>
                    <span className="ml-2 font-mono text-gray-700 break-all">{externalApiUrl}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Chain ID:</span>
                    <span className="ml-2 text-gray-700">{blockchainService?.chainId}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Last Response:</span>
                    <span className="ml-2 text-gray-700">
                      {stats.lastResponse ? stats.lastResponse.toLocaleTimeString() : 'Never'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Performance Metrics */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Performance</h4>
                <div className="space-y-1">
                  <div>
                    <span className="text-gray-500">Latency:</span>
                    <span className="ml-2 text-gray-700">{stats.latency != null ? `${stats.latency}ms` : 'Run diagnostics to measure'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Queue Length:</span>
                    <span className="ml-2 text-gray-700">{apiStatus.queueLength || 0}</span>
                  </div>
                </div>
              </div>

              {/* Status Details */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Status Details</h4>
                <div className="space-y-1">
                  <div>
                    <span className="text-gray-500">Circuit Breaker:</span>
                    <span className="ml-2 text-gray-700">
                      {apiStatus.status === 'circuit_open' ? 'Open' : 'Closed'}
                    </span>
                  </div>
                  {apiStatus.failureCount > 0 && (
                    <div>
                      <span className="text-gray-500">Recent Failures:</span>
                      <span className="ml-2 text-gray-700">{apiStatus.failureCount}</span>
                    </div>
                  )}
                  {apiStatus.nextRetry && (
                    <div>
                      <span className="text-gray-500">Next Retry:</span>
                      <span className="ml-2 text-gray-700">
                        {new Date(apiStatus.nextRetry).toLocaleTimeString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap items-center justify-between gap-2">
              {diagMessage ? (
                <span className={`text-xs ${
                  diagMessage.type === 'error' ? 'text-red-600' :
                  diagMessage.type === 'success' ? 'text-green-600' : 'text-gray-500'
                }`}>
                  {diagMessage.text}
                </span>
              ) : (
                <span className="text-xs text-gray-400">Click to test connectivity</span>
              )}
              <div className="flex space-x-2">
                <button
                  onClick={runDiagnostics}
                  className="btn-primary px-3 py-1.5 text-xs font-medium"
                  title="Run connectivity test"
                >
                  Run diagnostics
                </button>
                <button
                  onClick={() => setIsExpanded(false)}
                  className="btn-secondary px-3 py-1.5 text-xs font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </footer>
  );
}

export default Footer;
